import { clearTokens, getAccessToken, getRefreshToken, http, startTokenRefresh, stopTokenRefresh, storeTokens } from '@/utils/httpClient'
import { computed, ref } from 'vue'

import { db } from '@/database'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import { DEFAULT_PERMISSIONS_BY_ROLE } from '@/constants/users'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const userProfile = ref(null)
  const loading = ref(false)
  const error = ref(null)

  // Router
  const router = useRouter()

  // Computed
  const isAuthenticated = computed(() => {
    // In development mode, only return true if user is actually set
    if (import.meta.env.VITE_SKIP_AUTH === 'true') {
      return !!user.value
    }
    return !!user.value
  })
  const userRole = computed(() => {
    return userProfile.value?.role || 'tester'
  })
  const isAdmin = computed(() => userRole.value === 'admin')
  const isQAManager = computed(() => userRole.value === 'qa_manager')
  const isDeveloper = computed(() => userRole.value === 'developer')
  const isTester = computed(() => userRole.value === 'tester')

  // Actions
  const initializeAuth = async () => {
    loading.value = true
    
    try {
      // Skip authentication in development mode if VITE_SKIP_AUTH is true
      if (import.meta.env.VITE_SKIP_AUTH === 'true') {
        // Set a mock admin user
        user.value = {
          id: 'mock-user-1',
          uid: 'mock-user-1',
          email: 'admin@example.com',
          name: 'Admin User',
          displayName: 'Admin User',
          role: 'super_admin'
        }
        userProfile.value = {
          id: 'mock-user-1',
          email: 'admin@example.com',
          name: 'Admin User',
          role: 'super_admin'
        }
        // Assign default permissions based on role
        userProfile.value.permissions = DEFAULT_PERMISSIONS_BY_ROLE[userProfile.value.role] || []
        localStorage.setItem('user_session', JSON.stringify(user.value))
        loading.value = false
        return
      }
      
      // Check if user was logged out
      const wasLoggedOut = localStorage.getItem('user_logged_out')
      if (wasLoggedOut === 'true') {
        localStorage.removeItem('user_logged_out')
        loading.value = false
        return
      }
      
      // Check for existing access token
      const accessToken = getAccessToken()
      
      if (accessToken) {
        try {
          // Set auth token on database adapter (for API calls after page refresh)
          db.setAuthToken(accessToken)
          
          // Verify token is still valid by fetching current user
          const response = await http.get('/auth/me')
          
          if (response.user) {
            user.value = {
              id: response.user.id,
              uid: response.user.id,
              email: response.user.email,
              name: response.user.name,
              displayName: response.user.name,
              role: response.user.role
            }
            userProfile.value = response.user
            
            // Assign default permissions based on role
            userProfile.value.permissions = DEFAULT_PERMISSIONS_BY_ROLE[userProfile.value.role] || []
            
            // Update session storage
            localStorage.setItem('user_session', JSON.stringify(user.value))
            
            // Start automatic token refresh on page load
            startTokenRefresh()
          }
        } catch (error) {
          clearTokens()
          localStorage.removeItem('user_session')
        }
      }
      
    } catch (error) {
      clearTokens()
      localStorage.removeItem('user_session')
    } finally {
      loading.value = false
    }
  }

  const loadUserProfile = async (uid) => {
    try {
      const response = await http.get('/auth/me')
      if (response.user) {
        userProfile.value = response.user
        // Assign default permissions based on role
        userProfile.value.permissions = DEFAULT_PERMISSIONS_BY_ROLE[userProfile.value.role] || []
      }
    } catch (err) {
      // Silent error handling
    }
  }

  const signIn = async (email, password) => {
    loading.value = true
    error.value = null
    
    try {
      // Call API login endpoint
      const response = await http.post('/auth/login', { email, password })
      
      // Handle wrapped response format (NestJS)
      const data = response.data || response
      
      // Store tokens
      storeTokens(data.accessToken, data.refreshToken)
      
      // Set auth token on database adapter (for API calls)
      db.setAuthToken(data.accessToken)
      
      // Set user data
      user.value = {
        id: data.user.id,
        uid: data.user.id,
        email: data.user.email,
        name: data.user.name,
        displayName: data.user.name,
        role: data.user.role
      }
      userProfile.value = data.user
      
      // Assign default permissions based on role
      userProfile.value.permissions = DEFAULT_PERMISSIONS_BY_ROLE[userProfile.value.role] || []
      
      // Store session in localStorage and clear logout flag
      localStorage.setItem('user_session', JSON.stringify(user.value))
      localStorage.removeItem('user_logged_out')
      
      // Start automatic token refresh
      startTokenRefresh()
      
      // Get redirect URL if stored, otherwise return user for component to handle redirect
      const redirectUrl = localStorage.getItem('redirect_after_login')
      if (redirectUrl) {
        localStorage.removeItem('redirect_after_login')
        // Return user with redirect URL
        return { ...user.value, redirectUrl }
      }
      
      return user.value
      
    } catch (err) {
      error.value = getAuthErrorMessage(err.code || err.message)
      throw err
    } finally {
      loading.value = false
    }
  }

  const signUp = async (email, password, userData) => {
    loading.value = true
    error.value = null
    
    try {
      // Call API register endpoint
      const response = await http.post('/auth/register', {
        name: userData.name,
        email: email,
        password: password,
        role: userData.role || 'tester'
      })
      
      // Handle wrapped response format (NestJS)
      const data = response.data || response
      
      // Store tokens
      storeTokens(data.accessToken, data.refreshToken)
      
      // Set auth token on database adapter (for API calls)
      db.setAuthToken(data.accessToken)
      
      // Set user data
      user.value = {
        id: data.user.id,
        uid: data.user.id,
        email: data.user.email,
        name: data.user.name,
        displayName: data.user.name,
        role: data.user.role
      }
      userProfile.value = data.user
      
      // Assign default permissions based on role
      userProfile.value.permissions = DEFAULT_PERMISSIONS_BY_ROLE[userProfile.value.role] || []
      
      // Store session in localStorage and clear logout flag
      localStorage.setItem('user_session', JSON.stringify(user.value))
      localStorage.removeItem('user_logged_out')
      
      // Start automatic token refresh
      startTokenRefresh()
      
      return user.value
      
    } catch (err) {
      error.value = getAuthErrorMessage(err.code || err.message)
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    loading.value = true
    error.value = null
    
    try {
      
      // Get refresh token before clearing
      const refreshToken = getRefreshToken()
      
      // Store current URL before logout for redirect after login
      const currentPath = router.currentRoute.value.fullPath
      if (currentPath && currentPath !== '/login' && currentPath !== '/') {
        localStorage.setItem('redirect_after_login', currentPath)
      }
      
      // Call API logout endpoint (if we have a token)
      if (refreshToken) {
        try {
          await http.post('/auth/logout', { refreshToken })
        } catch (err) {
          // Silent error handling
        }
      }
      
      // Stop automatic token refresh
      stopTokenRefresh()
      
      // Set a logout flag to prevent session restoration
      localStorage.setItem('user_logged_out', 'true')
      
      // Clear tokens and session
      clearTokens()
      localStorage.removeItem('user_session')
      
      // Clear auth token from database adapter
      db.clearAuthToken()
      
      // Clear in-memory state
      user.value = null
      userProfile.value = null
      
      // Redirect to login page after logout
      router.push('/login')
      
    } catch (err) {
      error.value = 'Error signing out'
      
      // Even if there's an error, clear local state
      stopTokenRefresh()
      user.value = null
      userProfile.value = null
      clearTokens()
      localStorage.removeItem('user_session')
      localStorage.setItem('user_logged_out', 'true')
      
    } finally {
      loading.value = false
    }
  }

  const updateUserProfile = async (updates) => {
    try {
      const updatedUser = await http.patch(`/users/${user.value.id}`, updates)
      userProfile.value = updatedUser
      
      // Update user value
      user.value = {
        ...user.value,
        name: updatedUser.name,
        displayName: updatedUser.name
      }
      
      // Update session storage
      localStorage.setItem('user_session', JSON.stringify(user.value))
      
    } catch (err) {
      error.value = 'Error updating profile'
      throw err
    }
  }

  const changePassword = async ({ currentPassword, newPassword }) => {
    try {
      // Call the change password API endpoint
      await http.post('/auth/change-password', {
        currentPassword,
        newPassword
      })
      
    } catch (err) {
      error.value = err.message || 'Failed to change password'
      throw err
    }
  }

  const hasPermission = (permission) => {
    if (!userProfile.value?.permissions) return false
    return userProfile.value.permissions.includes(permission)
  }

  const canAccessProject = (projectId, requiredPermission = 'read') => {
    // For development/mock purposes, allow access to all projects
    // In a real application, this would check actual project permissions
    if (isAdmin.value) return true
    
    // For now, allow access to all projects for authenticated users
    // This can be replaced with actual permission checking later
    return true
    
    // Original implementation (commented out for development):
    // const projectAccess = userProfile.value?.projectAccess?.[projectId]
    // if (!projectAccess) return false
    // return projectAccess.includes(requiredPermission)
  }

  // Helper functions
  const getAuthErrorMessage = (errorCode) => {
    const errorMessages = {
      'INVALID_CREDENTIALS': 'Invalid email or password',
      'EMAIL_ALREADY_EXISTS': 'An account with this email already exists',
      'WEAK_PASSWORD': 'Password must be at least 6 characters',
      'INVALID_EMAIL': 'Invalid email address',
      'INVALID_EMAIL_DOMAIN': 'Only email addresses from @gohighlevel.com are allowed',
      'MISSING_FIELDS': 'Please fill in all required fields',
      'USER_NOT_FOUND': 'No account found with this email address',
      'INVALID_TOKEN': 'Your session has expired. Please login again',
      'NO_TOKEN': 'Authentication required',
      'REGISTRATION_ERROR': 'Registration failed. Please try again',
      'LOGIN_ERROR': 'Login failed. Please try again'
    }
    return errorMessages[errorCode] || 'An error occurred during authentication'
  }

  return {
    // State
    user,
    userProfile,
    loading,
    error,
    
    // Computed
    isAuthenticated,
    userRole,
    isAdmin,
    isQAManager,
    isDeveloper,
    isTester,
    
    // Actions
    initializeAuth,
    signIn,
    signUp,
    logout,
    updateUserProfile,
    changePassword,
    hasPermission,
    canAccessProject
  }
}) 